/**
 * reCAPTCHA Token Analysis Utility
 * 
 * This utility provides functions to analyze and debug reCAPTCHA tokens,
 * particularly useful for understanding token length variations and debugging
 * token validation issues.
 */

import { log } from './logger';

export interface TokenAnalysis {
  length: number;
  startsWithDigit: boolean;
  startsWithLetter: boolean;
  initialCharacters: string;
  containsSpecialChars: boolean;
  isValidLength: boolean;
  estimatedType: 'short' | 'standard' | 'long';
}

/**
 * Determine the estimated type of the token based on its length
 */
const getTokenType = (tokenLength: number): 'short' | 'standard' | 'long' => {
  if (tokenLength < 500) return 'short';
  if (tokenLength < 1000) return 'standard';
  return 'long';
};

/**
 * Analyze a reCAPTCHA token and return detailed information
 * @param token - The reCAPTCHA token to analyze
 * @param minLength - Minimum acceptable length (default: 600)
 * @returns Token analysis object
 */
export const analyzeToken = (token: string, minLength: number = 600): TokenAnalysis => {
  if (!token) {
    return {
      length: 0,
      startsWithDigit: false,
      startsWithLetter: false,
      initialCharacters: '',
      containsSpecialChars: false,
      isValidLength: false,
      estimatedType: 'short'
    };
  }

  const tokenLength = token.length;
  const analysis: TokenAnalysis = {
    length: tokenLength,
    startsWithDigit: /^\d/.test(token),
    startsWithLetter: /^[a-zA-Z]/.test(token),
    initialCharacters: token.substring(0, 10),
    containsSpecialChars: /[_-]/.test(token),
    isValidLength: tokenLength >= minLength,
    estimatedType: getTokenType(tokenLength)
  };

  return analysis;
};

/**
 * Generate a context string for logging
 */
const getContextString = (context: string): string => {
  return context ? ` (${context})` : '';
};

/**
 * Log detailed token analysis for debugging purposes
 * @param token - The reCAPTCHA token to analyze
 * @param context - Additional context for logging (e.g., 'initial attempt', 'retry 1')
 * @param minLength - Minimum acceptable length (default: 600)
 */
export const logTokenAnalysis = (token: string, context: string = '', minLength: number = 600): void => {
  const analysis = analyzeToken(token, minLength);
  const contextString = getContextString(context);
  const spacer = context ? '' : ' '; // Add space only when no context
  
  log.debug(`reCAPTCHA Token Analysis${contextString}${spacer}:`, {
    length: analysis.length,
    isValidLength: analysis.isValidLength,
    estimatedType: analysis.estimatedType,
    initialChars: analysis.initialCharacters,
    startsWithDigit: analysis.startsWithDigit,
    startsWithLetter: analysis.startsWithLetter,
    containsSpecialChars: analysis.containsSpecialChars
  });

  if (!analysis.isValidLength) {
    log.warn(`Token is shorter than required minimum (${analysis.length} < ${minLength})`);
  }
};

/**
 * Compare two reCAPTCHA tokens and log the differences
 * @param token1 - First token
 * @param token2 - Second token
 * @param labels - Optional labels for the tokens (default: ['Token 1', 'Token 2'])
 */
export const compareTokens = (token1: string, token2: string, labels: [string, string] = ['Token 1', 'Token 2']): void => {
  const analysis1 = analyzeToken(token1);
  const analysis2 = analyzeToken(token2);

  log.debug('reCAPTCHA Token Comparison:', {
    [labels[0]]: {
      length: analysis1.length,
      type: analysis1.estimatedType,
      initialChars: analysis1.initialCharacters,
      startsWithDigit: analysis1.startsWithDigit
    },
    [labels[1]]: {
      length: analysis2.length,
      type: analysis2.estimatedType,
      initialChars: analysis2.initialCharacters,
      startsWithDigit: analysis2.startsWithDigit
    },
    differences: {
      lengthDiff: Math.abs(analysis1.length - analysis2.length),
      typesSame: analysis1.estimatedType === analysis2.estimatedType,
      bothValidLength: analysis1.isValidLength && analysis2.isValidLength
    }
  });
};

/**
 * Validate if a token meets the minimum length requirements
 * @param token - The reCAPTCHA token to validate
 * @param minLength - Minimum acceptable length (default: 600)
 * @returns true if token is valid, false otherwise
 */
export const isTokenValid = (token: string, minLength: number = 600): boolean => {
  return Boolean(token) && token.length >= minLength;
};

/**
 * Generate a truncated token preview
 */
const generateTruncatedPreview = (token: string, previewLength: number): string => {
  const start = token.substring(0, previewLength);
  const end = token.substring(token.length - previewLength);
  const lengthInfo = ` (${token.length} chars)`;
  return `${start}...${end}${lengthInfo}`;
};

/**
 * Get a safe preview of a token for logging (shows only first/last few characters)
 * @param token - The reCAPTCHA token
 * @param previewLength - Number of characters to show from start and end (default: 8)
 * @returns Safe preview string
 */
export const getTokenPreview = (token: string, previewLength: number = 8): string => {
  if (!token) return '[empty]';
  if (token.length <= previewLength * 2) return token;
  
  return generateTruncatedPreview(token, previewLength);
};
